import {
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  TaskPatientData,
  TaskState,
  TaskType,
  isTaskTypeChatConcern,
  isTaskTypeSelfTestConcern,
  isTaskTypeToFollowup,
  useLazyGetTaskDetailQuery,
  useSetTaskFlaggedForSupervisorMutation,
} from "../features/data/patientApiSlice";
import { formatDate, formatDateTime } from "../utils/dates";

import ConcernPillList from "./ConcernPillList";
import DeleteTaskIcon from "@mui/icons-material/DeleteForeverOutlined";
import EntryContainer from "./EntryContainer";
import FlagTaskIcon from "@mui/icons-material/LabelImportantOutlined";
import FlaggedForSupervisorIcon from "../../assets/supervisor_review.svg?react";
import IconButton from "./IconButton";
import React from "react";
import TaskMenuIcon from "@mui/icons-material/MoreVert";
import { TaskTypeIcon } from "./TaskTypeIcon";
import { getErrorText } from "../utils/errors";
import { globalStyles } from "../utils/styles";
import { logEvent } from "../features/api/apiSlice";
import { useAppDispatch } from "../features/store";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import InlineTaskCompletion from "./InlineTaskCompletion";
import Notes from "./Notes";

export const getTaskElementId = (uid: string, expanded?: boolean) =>
  `task-${uid}-${expanded ? "e" : ""}`;

export enum TaskEditingState {
  done = 0,
  saving,
}

const TaskEntry = (props: {
  task: TaskPatientData;
  contained?: boolean;
  expandByDefault?: boolean;
  editingState?: TaskEditingState;
  selected?: boolean;
  onSelect?: (taskUid: string) => void;
  onAddNote?: (taskUid: string, content: string) => void;
  onComplete?: (completeData: { actionsTaken: string[]; notes: string }, task: TaskPatientData) => void;
  onDelete?: (task: TaskPatientData) => void;
}) => {
  const {
    task,
    contained,
    expandByDefault,
    editingState,
    selected,
    onSelect,
    onAddNote,
    onComplete,
    onDelete,
  } = props;
  const { t } = useTranslation("TaskEntry");
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Component state
  const [expanded, setExpanded] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

  // Queries/mutations
  const [getTaskDetail, taskDetailResult] = useLazyGetTaskDetailQuery();
  const [setTaskFlaggedForSupervisor] =
    useSetTaskFlaggedForSupervisorMutation();

  // Callbacks
  const handleExpandClick = useCallback(
    () => onSelect?.(task.uid),
    [onSelect, task.uid]
  );
  const handleExpanded = useCallback(() => {
    setExpanded(true);
    getTaskDetail({ uid: task.uid }, true);
  }, [getTaskDetail, task.uid]);
  const handleMenuClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      onSelect?.(task.uid);
      setMenuAnchor(e.currentTarget);
      e.stopPropagation();
    },
    [onSelect, task.uid]
  );
  const handleAddNote = useCallback(
    (content: string) => {
      onSelect?.(task.uid);
      onAddNote?.(task.uid, content);
    },
    [onSelect, onAddNote, task.uid]
  );
  const handleCompleteTask = useCallback(
    (completeData: { actionsTaken: string[]; notes: string }) => {
      onSelect?.(task.uid);
      onComplete?.(completeData, task);
    },
    [onSelect, onComplete, task]
  );
  const handleFlagForSupervisorClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      logEvent(dispatch, {
        event: "taskFlagged",
        data: { taskUid: task.uid, flagged: !task.flagged_for_supervisor },
      });
      setTaskFlaggedForSupervisor({
        uid: task.uid,
        flagged: !task.flagged_for_supervisor,
        patientUid: task.patientUid,
      });
      setMenuAnchor(null);
      e.stopPropagation();
    },
    [setTaskFlaggedForSupervisor, task, dispatch]
  );
  const handleDeleteTask = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      onSelect?.(task.uid);
      setMenuAnchor(null);
      e.stopPropagation();
      onDelete?.(task);
    },
    [onSelect, onDelete, task]
  );
  const handleMenuClose = useCallback((e: React.SyntheticEvent) => {
    setMenuAnchor(null);
    e.stopPropagation();
  }, []);

  const taskTitle =
    task.title ||
    (isTaskTypeSelfTestConcern(task.type)
      ? t("selfTestConcern")
      : isTaskTypeChatConcern(task.type)
      ? t("chatConcern")
      : isTaskTypeToFollowup(task.type)
      ? t("toFollowup")
      : t("taskTypes:" + task.type));
  const taskSubtitle = task.due_date
    ? t("dueDate", {
        date: formatDate(new Date(task.due_date)),
      })
    : formatDateTime(new Date(task.created));
  const isTaskTodo = task.state === TaskState.todo;
  return (
    <EntryContainer
      id={task.uid ? getTaskElementId(task.uid, expanded) : undefined}
      contained={contained}
      titleIcon={
        <TaskTypeIcon
          type={task.type}
          state={task.state}
          fontSize={contained ? "medium" : "large"}
        />
      }
      titleText={taskTitle}
      subtitleText={""}
      extraTitleComponent={
        <ConcernPillList
          tasks={[task]}
          singlePillHasText
          sx={{ marginLeft: 1 }}
        />
      }
      cornerTitleComponent={
        task.flagged_for_supervisor ? (
          <FlaggedForSupervisorIcon
            fill={theme.palette.flagForSupervisor.main}
          />
        ) : null
      }
      expandByDefault={expandByDefault}
      onExpandClicked={handleExpandClick}
      onExpanded={!editingState ? handleExpanded : undefined}
      menuComponent={
        isTaskTodo ? (
          <>
            <IconButton
              icon={<TaskMenuIcon />}
              tooltip={t("common:menu")}
              onClick={handleMenuClick}
            />
            <Menu
              anchorEl={menuAnchor}
              open={menuAnchor != null}
              onClose={handleMenuClose}
              slotProps={{
                list: {
                  role: "listbox",
                },
              }}
            >
              <MenuItem onClick={handleFlagForSupervisorClick}>
                <ListItemIcon>
                  <FlagTaskIcon fontSize="small" />
                </ListItemIcon>
                {t(
                  task.flagged_for_supervisor
                    ? "clearFlagForSupervisor"
                    : "flagForSupervisor"
                )}
              </MenuItem>
              {task.type === TaskType.followup ? (
                <MenuItem onClick={handleDeleteTask}>
                  <ListItemIcon>
                    <DeleteTaskIcon fontSize="small" />
                  </ListItemIcon>
                  {t("common:delete")}
                </MenuItem>
              ) : null}
            </Menu>
          </>
        ) : null
      }
      taskStateIndicator={task.state}
      selected={selected}
      isFetching={!taskDetailResult.data && taskDetailResult.isFetching}
      errorText={
        taskDetailResult.isError ? getErrorText(taskDetailResult.error) : ""
      }
      isSaving={editingState === TaskEditingState.saving}
      tooltipBody={
        <Typography variant="detailEntryBody">{taskSubtitle}</Typography>
      }
      completeTaskComponent={
        <>
          {taskDetailResult.data ? (
            <Stack>
              {taskDetailResult.data?.description ? (
                <Typography variant="detailEntryBody" sx={globalStyles.gapTop}>
                  {task.due_date &&
                    t("dueDate", {
                      date: formatDate(new Date(task.due_date)),
                    })}
                  {task.due_date && taskDetailResult.data.description && " | "}
                  {taskDetailResult.data.description}
                </Typography>
              ) : null}

              {!!taskDetailResult.data?.created_by &&
              !!taskDetailResult.data?.created ? (
                <Typography
                  variant="detailCellSubtitle"
                  sx={globalStyles.gapTop}
                >
                  {t("createdInfo", {
                    name: taskDetailResult.data.created_by.name,
                    date: formatDateTime(
                      new Date(taskDetailResult.data.created)
                    ),
                  })}
                </Typography>
              ) : null}
              {!isTaskTodo && taskDetailResult.data?.notes.length > 0 ? (
                <Notes
                  notes={taskDetailResult.data?.notes}
                  onAddNote={undefined}
                />
              ) : null}
            </Stack>
          ) : null}
          {isTaskTodo ? (
            <InlineTaskCompletion
              taskType={task.type}
              onAddNote={handleAddNote}
              onComplete={handleCompleteTask}
              notes={taskDetailResult.data?.notes ?? []}
            />
          ) : taskDetailResult.data ? (
            <Stack>
              {task.state === TaskState.complete ? (
                <Typography
                  variant="detailCellSubtitle"
                  sx={{ ...globalStyles.gapTop, alignSelf: "flex-end" }}
                >
                  {task.type === TaskType.request_to_chat &&
                  !taskDetailResult.data?.completed_by
                    ? t("autoCompletedInfo", {
                        name: "Clinical Portal",
                        date: taskDetailResult.data?.completed
                          ? formatDateTime(
                              new Date(taskDetailResult.data.completed)
                            )
                          : t("common:unknown"),
                      })
                    : t("completedInfo", {
                        name:
                          taskDetailResult.data?.completed_by?.name ||
                          t("common:unknown"),
                        actionsTaken:
                          taskDetailResult.data?.actions_taken.length > 0
                            ? taskDetailResult.data?.actions_taken
                                .map((action) =>
                                  t("common:actionsTaken:" + action)
                                )
                                .join(", ")
                            : t("common:unknown"),
                        date: taskDetailResult.data?.completed
                          ? formatDateTime(
                              new Date(taskDetailResult.data.completed)
                            )
                          : t("common:unknown"),
                      })}
                </Typography>
              ) : task.state === TaskState.deleted ? (
                <Typography
                  variant="detailCellSubtitle"
                  sx={{ ...globalStyles.gapTop, alignSelf: "flex-end" }}
                >
                  {t("deletedInfo", {
                    name:
                      taskDetailResult.data?.deleted_by?.name ||
                      t("common:unknown"),
                    date: taskDetailResult.data?.deleted
                      ? formatDateTime(new Date(taskDetailResult.data.deleted))
                      : t("common:unknown"),
                  })}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </>
      }
      showToggle={false}
    />
  );
};

export default TaskEntry;
